// ../components/ExternalPatientConsentDashboardModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Stack,
    Divider,
    TablePagination,
    Card,
    CardContent,
    Grid,
    IconButton,
    Menu,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from "@mui/material";
import { useAuth } from "../../components/TokenHandler";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

import InformedConsentModal from "../InformedConsentModal";
import LinkToPatientModal from "../../components/LinkToPatientModal";

const modalStyle = {
    bgcolor: "background.paper",
    p: 3,
    mx: "auto",
    mt: 2,
    width: "96vw",
    height: "92vh",
    maxWidth: "none",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
};

const signatureModalStyle = {
    bgcolor: "background.paper",
    p: 2,
    mx: "auto",
    mt: 6,
    width: "90%",
    maxWidth: 900,
    borderRadius: 2,
};

const editModalStyle = {
    bgcolor: "background.paper",
    p: 3,
    mx: "auto",
    mt: 6,
    width: "92%",
    maxWidth: 700,
    borderRadius: 2,
};

const FILTERS = {
    pending: { label: "Pending", pending: true, active: true },
    completed: { label: "Completed", pending: false, active: true },
    discarded: { label: "Discarded", pending: false, active: false },
};

// Placeholder dropdown options
const FACILITY_PROVIDER_OPTIONS = [
    { facility_name: "Pinnacle Canton", provider_number: "123456" },
    { facility_name: "Cedarstone Care Pavilion", provider_number: "104209" },
    { facility_name: "Evergreen Health Center", provider_number: "889900" },
    { facility_name: "Maplewood Skilled Nursing", provider_number: "554433" },
    { facility_name: "Riverbend Rehab", provider_number: "778811" },
];

function buildUrl({ name, pending, active, page, pageSize }) {
    const base = `${process.env.REACT_APP_API_URL}/consent/view-external-forms`;
    const params = new URLSearchParams();
    params.set("name", name ?? "");
    params.set("pending", String(!!pending));
    params.set("active", active === null || active === undefined ? "" : String(!!active));
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    return `${base}?${params.toString()}`;
}

function normalizeResponse(json, fallbackPage, fallbackPageSize) {
    const items = json?.rows ?? json?.items ?? json?.data ?? json?.results ?? json?.forms ?? [];
    const total = json?.total ?? json?.count ?? json?.totalCount ?? items?.length ?? 0;
    const page = json?.page ?? fallbackPage;
    const pageSize = json?.pageSize ?? fallbackPageSize;
    return { items, total, page, pageSize };
}

function formatDob(dob) {
    if (!dob) return "—";
    if (typeof dob === "string") return dob.slice(0, 10);
    try {
        return new Date(dob).toISOString().slice(0, 10);
    } catch {
        return String(dob).slice(0, 10);
    }
}

function formatTimestamp(ts) {
    if (!ts) return "—";
    try {
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return String(ts);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    } catch {
        return String(ts);
    }
}

function pdfUrl(id) {
    if (!id) return null;
    return `${process.env.REACT_APP_API_URL}/consent/external-form/${id}/signature`;
}

function toFacilityProviderValue(facility_name, provider_number) {
    return `${facility_name}|||${provider_number}`;
}

function fromFacilityProviderValue(value) {
    const [facility_name, provider_number] = String(value || "").split("|||");
    return { facility_name, provider_number };
}

function hasAnyCorrected(row) {
    return Boolean(
        row?.corrected_patient_first_name ||
        row?.corrected_patient_last_name ||
        row?.corrected_patient_dob ||
        row?.corrected_facility_name ||
        row?.corrected_provider_number
    );
}

function pickDisplay(row) {
    return {
        patient_first_name: row?.corrected_patient_first_name || row?.patient_first_name || "",
        patient_last_name: row?.corrected_patient_last_name || row?.patient_last_name || "",
        patient_dob: row?.corrected_patient_dob || row?.patient_dob || null,
        facility_name: row?.corrected_facility_name || row?.facility_name || "",
        provider_number: row?.corrected_provider_number || row?.provider_number || "",
    };
}

function OriginalInfoTooltip({ row }) {
    if (!hasAnyCorrected(row)) return null;

    const originalName =
        `${row?.patient_first_name ?? ""} ${row?.patient_last_name ?? ""}`.trim() || "—";
    const correctedName =
        `${row?.corrected_patient_first_name ?? ""} ${row?.corrected_patient_last_name ?? ""}`.trim() ||
        "—";

    return (
        <Box sx={{ p: 0.75, maxWidth: 360 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
                Original Values
            </Typography>

            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.35 }}>
                <strong>Name:</strong> {originalName}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.35 }}>
                <strong>DOB:</strong> {row?.patient_dob ? String(row.patient_dob).slice(0, 10) : "—"}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.35 }}>
                <strong>Facility:</strong> {row?.facility_name || "—"}{" "}
                {row?.provider_number ? `(${row.provider_number})` : ""}
            </Typography>

            <Divider sx={{ my: 0.75 }} />

            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
                Corrected Values (shown on card)
            </Typography>

            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.35 }}>
                <strong>Name:</strong> {correctedName}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.35 }}>
                <strong>DOB:</strong>{" "}
                {row?.corrected_patient_dob ? String(row.corrected_patient_dob).slice(0, 10) : "—"}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.35 }}>
                <strong>Facility:</strong> {row?.corrected_facility_name || "—"}{" "}
                {row?.corrected_provider_number ? `(${row.corrected_provider_number})` : ""}
            </Typography>
        </Box>
    );
}

export default function ExternalPatientConsentDashboardModal({ open, onClose }) {
    const { keycloak: kc } = useAuth();

    const [linkOpen, setLinkOpen] = useState(false);
    const [linkExternalFormRow, setLinkExternalFormRow] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [submittedSearch, setSubmittedSearch] = useState("");

    const [filterKey, setFilterKey] = useState("pending");

    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(25);

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [actionLoadingId, setActionLoadingId] = useState(null);

    const abortRef = useRef(null);

    // Legacy iframe PDF modal
    const [pdfOpenLegacy, setPdfOpenLegacy] = useState(false);
    const [pdfSrcLegacy, setPdfSrcLegacy] = useState(null);
    const [pdfTitleLegacy, setPdfTitleLegacy] = useState("");

    // Auth-fetched blob URLs
    const [pdfBlobUrlLegacy, setPdfBlobUrlLegacy] = useState(null);
    const [modalPdfSrc, setModalPdfSrc] = useState(null);

    // per-card action menu
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);

    // EDIT PATIENT modal state
    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editFirst, setEditFirst] = useState("");
    const [editLast, setEditLast] = useState("");
    const [editDob, setEditDob] = useState("");
    const [editFacilityProvider, setEditFacilityProvider] = useState("");
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editError, setEditError] = useState("");

    // View PDF (InformedConsentModal) state
    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfRow, setPdfRow] = useState(null);

    const filter = FILTERS[filterKey];
    const backendPage = pageIndex + 1;

    const url = useMemo(() => {
        return buildUrl({
            name: submittedSearch,
            pending: filter.pending,
            active: filter.active,
            page: backendPage,
            pageSize,
        });
    }, [submittedSearch, filter.pending, filter.active, backendPage, pageSize]);

    const revokeLegacyBlobUrl = () => {
        if (pdfBlobUrlLegacy) {
            URL.revokeObjectURL(pdfBlobUrlLegacy);
            setPdfBlobUrlLegacy(null);
        }
    };

    const revokeModalBlobUrl = () => {
        if (modalPdfSrc) {
            URL.revokeObjectURL(modalPdfSrc);
            setModalPdfSrc(null);
        }
    };

    const loadPdfWithToken = async (id) => {
        const url = pdfUrl(id);
        if (!url) return null;

        const resp = await fetch(url, {
            headers: {
                Authorization: `Bearer ${kc?.token}`,
            },
        });

        if (!resp.ok) {
            const text = await resp.text().catch(() => "");
            throw new Error(text || `Failed to load PDF (${resp.status})`);
        }

        const blob = await resp.blob();
        return URL.createObjectURL(blob);
    };

    const fetchData = async () => {
        if (!open) return;

        setLoading(true);
        setError("");

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const resp = await fetch(url, {
                signal: controller.signal,
                headers: {
                    Authorization: `Bearer ${kc?.token}`,
                },
            });

            if (!resp.ok) {
                const text = await resp.text().catch(() => "");
                throw new Error(text || `Request failed (${resp.status})`);
            }

            const json = await resp.json();
            const normalized = normalizeResponse(json, backendPage, pageSize);
            setItems(Array.isArray(normalized.items) ? normalized.items : []);
            setTotal(Number.isFinite(+normalized.total) ? +normalized.total : 0);
        } catch (e) {
            if (e?.name === "AbortError") return;
            setError(e?.message || "Something went wrong while loading external forms.");
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;

        setSearchText("");
        setSubmittedSearch("");
        setFilterKey("pending");
        setPageIndex(0);
        setPageSize(25);

        setPdfOpenLegacy(false);
        setPdfSrcLegacy(null);
        setPdfTitleLegacy("");
        revokeLegacyBlobUrl();

        setMenuAnchorEl(null);
        setMenuRow(null);

        setActionLoadingId(null);

        setEditOpen(false);
        setEditId(null);
        setEditFirst("");
        setEditLast("");
        setEditDob("");
        setEditFacilityProvider("");
        setEditSubmitting(false);
        setEditError("");

        setPdfOpen(false);
        setPdfRow(null);
        revokeModalBlobUrl();

        setLinkOpen(false);
        setLinkExternalFormRow(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, url, kc?.token]);

    useEffect(() => {
        return () => {
            if (abortRef.current) abortRef.current.abort();
            if (pdfBlobUrlLegacy) URL.revokeObjectURL(pdfBlobUrlLegacy);
            if (modalPdfSrc) URL.revokeObjectURL(modalPdfSrc);
        };
    }, [pdfBlobUrlLegacy, modalPdfSrc]);

    const handleSearch = () => {
        setPageIndex(0);
        setSubmittedSearch(searchText.trim());
    };

    const handleClear = () => {
        setSearchText("");
        setPageIndex(0);
        setSubmittedSearch("");
    };

    const handleFilterChange = (newKey) => {
        setFilterKey(newKey);
        setPageIndex(0);
    };

    const openLegacyPdfViewer = async (row) => {
        try {
            const blobUrl = await loadPdfWithToken(row?.id);
            if (!blobUrl) return;

            revokeLegacyBlobUrl();

            const display = pickDisplay(row);
            const name = `${display.patient_first_name ?? ""} ${display.patient_last_name ?? ""}`.trim();

            setPdfTitleLegacy(name ? `PDF: ${name}` : "PDF");
            setPdfBlobUrlLegacy(blobUrl);
            setPdfSrcLegacy(blobUrl);
            setPdfOpenLegacy(true);
        } catch (e) {
            setError(e?.message || "Failed to open PDF.");
        }
    };

    const closeLegacyPdf = () => {
        setPdfOpenLegacy(false);
        setPdfSrcLegacy(null);
        setPdfTitleLegacy("");
        revokeLegacyBlobUrl();
    };

    const openPdf = async (row) => {
        try {
            const blobUrl = await loadPdfWithToken(row?.id);
            if (!blobUrl) return;

            revokeModalBlobUrl();

            setModalPdfSrc(blobUrl);
            setPdfRow(row);
            setPdfOpen(true);
        } catch (e) {
            setError(e?.message || "Failed to open PDF.");
        }
    };

    const closePdf = () => {
        setPdfOpen(false);
        setPdfRow(null);
        revokeModalBlobUrl();
    };

    const openActionsMenu = (e, row) => {
        setMenuAnchorEl(e.currentTarget);
        setMenuRow(row);
    };

    const closeActionsMenu = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const statusToFlags = (status) => {
        if (status === "pending") return { pending: true, active: true };
        if (status === "completed") return { pending: false, active: true };
        if (status === "discard") return { pending: false, active: false };
        return {};
    };

    const postStatusChange = async ({ id, status }) => {
        const endpoint = `${process.env.REACT_APP_API_URL}/consent/change-form-status`;
        const resp = await fetch(endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${kc?.token}`,
            },
            body: JSON.stringify({ id, status }),
        });

        if (!resp.ok) {
            const text = await resp.text().catch(() => "");
            throw new Error(text || `Status update failed (${resp.status})`);
        }

        return true;
    };

    const applyLocalStatusUpdate = (id, status) => {
        const patch = statusToFlags(status);

        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

        const current = FILTERS[filterKey];
        const stillMatches = patch.pending === current.pending && patch.active === current.active;

        if (!stillMatches) {
            setItems((prev) => prev.filter((it) => it.id !== id));
            setTotal((t) => Math.max(0, t - 1));
        }
    };

    const openEditPatient = (row) => {
        if (!row?.id) return;

        const display = pickDisplay(row);

        setEditError("");
        setEditId(row.id);
        setEditFirst(display.patient_first_name ?? "");
        setEditLast(display.patient_last_name ?? "");
        setEditDob(formatDob(display.patient_dob) === "—" ? "" : formatDob(display.patient_dob));

        const currentVal =
            display.facility_name && display.provider_number
                ? toFacilityProviderValue(display.facility_name, display.provider_number)
                : toFacilityProviderValue(
                    FACILITY_PROVIDER_OPTIONS[0].facility_name,
                    FACILITY_PROVIDER_OPTIONS[0].provider_number
                );

        setEditFacilityProvider(currentVal);
        setEditOpen(true);
    };

    const closeEditPatient = () => {
        setEditOpen(false);
        setEditSubmitting(false);
        setEditError("");
    };

    const submitEditPatient = async () => {
        if (!editId) return;

        setEditSubmitting(true);
        setEditError("");

        const { facility_name, provider_number } = fromFacilityProviderValue(editFacilityProvider);

        const payload = {
            id: editId,
            patient_first_name: editFirst?.trim() || "",
            patient_last_name: editLast?.trim() || "",
            patient_dob: editDob || "",
            facility_name: facility_name || "",
            provider_number: provider_number || "",
        };

        try {
            const endpoint = `${process.env.REACT_APP_API_URL}/consent/edit-form`;
            const resp = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${kc?.token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!resp.ok) {
                const text = await resp.text().catch(() => "");
                throw new Error(text || `Edit failed (${resp.status})`);
            }

            setItems((prev) =>
                prev.map((it) => {
                    if (it.id !== editId) return it;

                    return {
                        ...it,
                        corrected_patient_first_name: payload.patient_first_name,
                        corrected_patient_last_name: payload.patient_last_name,
                        corrected_patient_dob: payload.patient_dob,
                        corrected_facility_name: payload.facility_name,
                        corrected_provider_number: payload.provider_number,
                        updated_on: new Date().toISOString(),
                    };
                })
            );

            closeEditPatient();
        } catch (e) {
            setEditError(e?.message || "Failed to save edits.");
        } finally {
            setEditSubmitting(false);
        }
    };

    const handleAction = async (action) => {
        const row = menuRow;
        closeActionsMenu();
        if (!row?.id) return;

        try {
            if (action === "viewPdf") {
                await openPdf(row);
                return;
            }

            if (action === "editPatient") {
                openEditPatient(row);
                return;
            }

            if (action === "completed") {
                setActionLoadingId(row.id);
                await postStatusChange({ id: row.id, status: "completed" });
                applyLocalStatusUpdate(row.id, "complete");
                return;
            }

            if (action === "discard") {
                setActionLoadingId(row.id);
                await postStatusChange({ id: row.id, status: "discarded" });
                applyLocalStatusUpdate(row.id, "discard");
                return;
            }

            if (action === "moveToPending") {
                setActionLoadingId(row.id);
                await postStatusChange({ id: row.id, status: "pending" });
                applyLocalStatusUpdate(row.id, "pending");
                return;
            }

            if (action === "linkToConsent") {
                setLinkExternalFormRow(row);
                setLinkOpen(true);
                return;
            }
        } catch (e) {
            setError(e?.message || "Action failed.");
        } finally {
            setActionLoadingId(null);
        }
    };

    const labelValueRow = (aLabel, aValue, bLabel, bValue, cLabel, cValue) => (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                    {aLabel}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    {aValue}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                    {bLabel}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    {bValue}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                    {cLabel}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    {cValue}
                </Typography>
            </Grid>
        </Grid>
    );

    const showPendingMenu = filterKey === "pending";
    const showCompletedOrDiscardedMenu = filterKey === "completed" || filterKey === "discarded";

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box sx={modalStyle}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Typography variant="h6">External Patient Consent Dashboard</Typography>
                        <Button variant="contained" onClick={onClose}>
                            Close
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        sx={{ mb: 2 }}
                    >
                        <TextField
                            label="Search by first and/or last name"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            fullWidth
                        />

                        <FormControl sx={{ minWidth: 220 }}>
                            <InputLabel id="ext-consent-filter-label">Filter</InputLabel>
                            <Select
                                labelId="ext-consent-filter-label"
                                label="Filter"
                                value={filterKey}
                                onChange={(e) => handleFilterChange(e.target.value)}
                            >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="discarded">Discarded</MenuItem>
                            </Select>
                        </FormControl>

                        <Stack direction="row" spacing={1} sx={{ whiteSpace: "nowrap" }}>
                            <Button variant="outlined" onClick={handleSearch}>
                                Search
                            </Button>
                            <Button variant="text" onClick={handleClear}>
                                Clear
                            </Button>
                        </Stack>
                    </Stack>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && (
                        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && items.length === 0 && !error && (
                        <Alert severity="info">No forms found for the selected filter/search.</Alert>
                    )}

                    {!loading && items.length > 0 && (
                        <Box sx={{ flex: 1, overflow: "auto", pr: 1 }}>
                            <Grid container spacing={2}>
                                {items.map((row, idx) => {
                                    const display = pickDisplay(row);
                                    const corrected = hasAnyCorrected(row);

                                    const patientName =
                                        `${display.patient_first_name ?? ""} ${display.patient_last_name ?? ""}`.trim() || "—";

                                    const facilityName = display.facility_name ?? "—";
                                    const providerNumber = display.provider_number ?? "—";

                                    const optIn = row?.optin === true ? "Yes" : row?.optin === false ? "No" : "—";

                                    const dob = formatDob(display.patient_dob);
                                    const patientPhone = row?.patient_phone_number ?? "—";
                                    const patientPhoneType = row?.patient_phone_number_type ?? "—";
                                    const patientEmail = row?.patient_email ?? "—";

                                    const proxyName =
                                        row?.proxy_first_name || row?.proxy_last_name
                                            ? `${row?.proxy_first_name ?? ""} ${row?.proxy_last_name ?? ""}`.trim()
                                            : "—";

                                    const proxyRel = row?.proxy_relationship ?? "—";
                                    const proxyPhone = row?.proxy_phone_number ?? "—";
                                    const proxyPhoneType = row?.proxy_phone_number_type ?? "—";
                                    const proxyEmail = row?.proxy_email ?? "—";

                                    const updated = formatTimestamp(row?.updated_on);
                                    const created = formatTimestamp(row?.created_on);

                                    const disableActionsForThisCard = actionLoadingId === row?.id;

                                    const header = (
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                                {patientName}
                                            </Typography>

                                            <IconButton
                                                size="small"
                                                onClick={(e) => openActionsMenu(e, row)}
                                                title="Actions"
                                                disabled={disableActionsForThisCard}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </Stack>
                                    );

                                    const hasPdf =
                                        row?.has_pdf === true ||
                                        (typeof row?.minio_key === "string" && row.minio_key.trim().length > 0);

                                    return (
                                        <Grid item xs={12} md={6} key={row?.id ?? idx}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: 2,
                                                    opacity: disableActionsForThisCard ? 0.85 : 1,
                                                    bgcolor: corrected ? "rgba(144, 238, 144, 0.25)" : "background.paper",
                                                }}
                                            >
                                                <CardContent>
                                                    {corrected ? (
                                                        <Tooltip title={<OriginalInfoTooltip row={row} />} arrow placement="top-start">
                                                            <Box>{header}</Box>
                                                        </Tooltip>
                                                    ) : (
                                                        header
                                                    )}

                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        {facilityName}
                                                    </Typography>

                                                    {labelValueRow("Facility", facilityName, "Provider #", providerNumber, "Opt-in", optIn)}
                                                    {labelValueRow("DOB", dob, "Patient Phone", patientPhone, "Patient Email", patientEmail)}
                                                    {labelValueRow(
                                                        "Patient Phone Type",
                                                        patientPhoneType,
                                                        "Proxy Phone",
                                                        proxyPhone,
                                                        "Proxy Phone Type",
                                                        proxyPhoneType
                                                    )}

                                                    <Box sx={{ height: 12 }} />

                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                        Proxy
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                        {proxyName}
                                                    </Typography>

                                                    {labelValueRow("Relationship", proxyRel, "Proxy Email", proxyEmail, "PDF Stored", hasPdf ? "Yes" : "No")}

                                                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Updated
                                                            </Typography>
                                                            <Typography variant="body2">{updated}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Created
                                                            </Typography>
                                                            <Typography variant="body2">{created}</Typography>
                                                        </Grid>
                                                    </Grid>

                                                    {disableActionsForThisCard && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                                                            Updating...
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}

                    <TablePagination
                        component="div"
                        count={total}
                        page={pageIndex}
                        onPageChange={(e, newPage) => setPageIndex(newPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(e) => {
                            setPageSize(parseInt(e.target.value, 10));
                            setPageIndex(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{ mt: 1 }}
                    />
                </Box>
            </Modal>

            <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={closeActionsMenu}>
                <MenuItem disableGutters sx={{ px: 1.5, py: 0.75 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon fontSize="small" />}
                        onClick={() => {
                            if (!menuRow) return;
                            closeActionsMenu();
                            openLegacyPdfViewer(menuRow);
                        }}
                        fullWidth
                        disabled={
                            !menuRow?.id ||
                            !(
                                menuRow?.has_pdf === true ||
                                (typeof menuRow?.minio_key === "string" && menuRow.minio_key.trim().length > 0)
                            )
                        }
                        sx={{
                            justifyContent: "flex-start",
                            textTransform: "none",
                        }}
                    >
                        View PDF
                    </Button>
                </MenuItem>

                {showPendingMenu && (
                    <>
                        <Divider />

                        <MenuItem onClick={() => handleAction("editPatient")}>
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Edit Patient</ListItemText>
                        </MenuItem>

                        <MenuItem onClick={() => handleAction("linkToConsent")}>
                            <ListItemIcon>
                                <LinkIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Link To Patient Consent</ListItemText>
                        </MenuItem>

                        <Divider />

                        <MenuItem onClick={() => handleAction("completed")}>
                            <ListItemIcon>
                                <CheckCircleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Completed</ListItemText>
                        </MenuItem>

                        <MenuItem onClick={() => handleAction("discard")}>
                            <ListItemIcon>
                                <BlockIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Discard</ListItemText>
                        </MenuItem>
                    </>
                )}

                {showCompletedOrDiscardedMenu && (
                    <>
                        <Divider />
                        <MenuItem onClick={() => handleAction("moveToPending")}>
                            <ListItemIcon>
                                <PendingActionsIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Move to Pending</ListItemText>
                        </MenuItem>
                    </>
                )}
            </Menu>

            <Modal open={pdfOpenLegacy} onClose={closeLegacyPdf}>
                <Box sx={signatureModalStyle}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Typography variant="h6">{pdfTitleLegacy}</Typography>
                        <Button variant="contained" onClick={closeLegacyPdf}>
                            Close
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {!pdfSrcLegacy ? (
                        <Alert severity="info">No PDF available.</Alert>
                    ) : (
                        <Box
                            sx={{
                                width: "100%",
                                height: "70vh",
                                border: "1px solid rgba(0,0,0,0.15)",
                                borderRadius: 2,
                                overflow: "hidden",
                            }}
                        >
                            <iframe
                                title="External Form PDF"
                                src={pdfSrcLegacy}
                                style={{ width: "100%", height: "100%", border: 0 }}
                            />
                        </Box>
                    )}
                </Box>
            </Modal>

            <Modal open={editOpen} onClose={closeEditPatient}>
                <Box sx={editModalStyle}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Typography variant="h6">Edit Patient</Typography>
                        <Button variant="outlined" onClick={closeEditPatient} disabled={editSubmitting}>
                            Close
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {editError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {editError}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Patient First Name"
                                value={editFirst}
                                onChange={(e) => setEditFirst(e.target.value)}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Patient Last Name"
                                value={editLast}
                                onChange={(e) => setEditLast(e.target.value)}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Patient DOB"
                                type="date"
                                value={editDob}
                                onChange={(e) => setEditDob(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            {/*
                            <FormControl fullWidth>
                                <InputLabel id="facility-provider-label">Facility + Provider #</InputLabel>
                                <Select
                                    labelId="facility-provider-label"
                                    label="Facility + Provider #"
                                    value={editFacilityProvider}
                                    onChange={(e) => setEditFacilityProvider(e.target.value)}
                                >
                                    {FACILITY_PROVIDER_OPTIONS.map((opt) => {
                                        const val = toFacilityProviderValue(opt.facility_name, opt.provider_number);
                                        return (
                                            <MenuItem key={val} value={val}>
                                                {opt.facility_name} ({opt.provider_number})
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            */}
                        </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: "flex-end" }}>
                        <Button variant="text" onClick={closeEditPatient} disabled={editSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={submitEditPatient} disabled={editSubmitting || !editId}>
                            {editSubmitting ? "Saving..." : "Submit"}
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            <LinkToPatientModal
                open={linkOpen}
                onClose={() => setLinkOpen(false)}
                externalFormRow={linkExternalFormRow}
                onLinked={({ id, statusResponse }) => {
                    applyLocalStatusUpdate(id, "complete");

                    const updatedOn = statusResponse?.row?.updated_on;
                    if (updatedOn) {
                        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, updated_on: updatedOn } : it)));
                    }
                }}
            />

            <InformedConsentModal
                open={pdfOpen}
                onClose={closePdf}
                patientOpt={pdfRow?.optin === true ? "optin" : pdfRow?.optin === false ? "optout" : ""}
                signedOn={pdfRow?.created_on}
                signatureSrc={modalPdfSrc}
                patient={{
                    name: pdfRow
                        ? `${pickDisplay(pdfRow)?.patient_first_name ?? ""} ${pickDisplay(pdfRow)?.patient_last_name ?? ""}`.trim()
                        : "",
                    dob: pdfRow ? pickDisplay(pdfRow).patient_dob : null,
                    phone: pdfRow?.patient_phone_number,
                    email: pdfRow?.patient_email,
                    facility_name: pdfRow ? pickDisplay(pdfRow).facility_name : "",
                    provider_number: pdfRow ? pickDisplay(pdfRow).provider_number : "",
                    phone_type: pdfRow?.patient_phone_number_type || "",
                }}
                proxy={
                    pdfRow &&
                    (pdfRow?.proxy_first_name ||
                        pdfRow?.proxy_last_name ||
                        pdfRow?.proxy_phone_number ||
                        pdfRow?.proxy_email ||
                        pdfRow?.proxy_relationship)
                        ? {
                            name: `${pdfRow?.proxy_first_name ?? ""} ${pdfRow?.proxy_last_name ?? ""}`.trim(),
                            phone: pdfRow?.proxy_phone_number,
                            email: pdfRow?.proxy_email,
                            relationship: pdfRow?.proxy_relationship,
                            phone_type: pdfRow?.proxy_phone_number_type || "",
                        }
                        : null
                }
                autoGeneratePdf={true}
            />
        </>
    );
}